import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { authService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet';

const ExtensionAuthSuccess = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const { organizations, currentOrganization, switchOrganization } = useOrganization();
  
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if we already have a token from the URL
  const urlTokenId = searchParams.get('tokenId');
  
  useEffect(() => {
    if (urlTokenId) {
      setTokenId(urlTokenId);
    } else if (organizations.length === 1) {
      // If user has only one organization, automatically select it
      setSelectedOrgId(organizations[0].id);
    } else if (currentOrganization) {
      // Pre-select current organization
      setSelectedOrgId(currentOrganization.id);
    }
  }, [urlTokenId, organizations, currentOrganization]);
  
  const handleCreateToken = async () => {
    if (!user || !selectedOrgId) return;
    
    setIsCreatingToken(true);
    setError(null);
    
    try {
      console.log('Creating token for extension auth:', {
        userId: user.uid,
        userEmail: user.email,
        selectedOrgId,
        selectedOrgName: organizations.find(org => org.id === selectedOrgId)?.name
      });
      
      // Create the auth token with the selected organization
      const response = await authService.createExtensionAuthToken({ organization_id: selectedOrgId });
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create extension token');
      }
      
      const newTokenId = response.data.token_id;
      setTokenId(newTokenId);
      
      console.log('Token created successfully, updating UI with tokenId:', newTokenId);
      
      // Update URL with tokenId
      setSearchParams({ tokenId: newTokenId });
      
      // If the selected org is different from current, switch to it
      if (selectedOrgId !== currentOrganization?.id) {
        console.log('Switching organization context to:', selectedOrgId);
        await switchOrganization(selectedOrgId);
      }
    } catch (error) {
      console.error('Error creating extension auth token:', error);
      setError('Failed to create authentication token. Please try again.');
    } finally {
      setIsCreatingToken(false);
    }
  };
  
  // If user is not authenticated, redirect to login
  useEffect(() => {
    if (!user && !urlTokenId) {
      navigate('/login?from=extension');
    }
  }, [user, urlTokenId, navigate]);
  
  // If we already have a token, show success
  if (tokenId) {
    return (
      <>
        <Helmet>
          <title>Extension Authentication Successful | Hobbes</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 text-green-600 dark:text-green-400">
                <CheckCircle className="h-full w-full" />
              </div>
              <CardTitle className="text-2xl font-bold">Authentication Successful!</CardTitle>
              <CardDescription>
                You can close this tab and return to the extension.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                The extension will automatically detect your authentication.
              </p>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">Token ID:</p>
                <p className="font-mono text-sm break-all">{tokenId}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
  
  // Show organization selection
  return (
    <>
      <Helmet>
        <title>Select Organization | Hobbes</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Select Organization</CardTitle>
            <CardDescription>
              Choose which organization to use with the Hobbes extension
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-md mb-4">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label htmlFor="organization" className="text-sm font-medium block mb-2">
                  Organization
                </label>
                <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                  <SelectTrigger id="organization">
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleCreateToken} 
                disabled={!selectedOrgId || isCreatingToken}
                className="w-full"
              >
                {isCreatingToken ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Token...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
              This will grant the extension access to the selected organization's data.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ExtensionAuthSuccess;